import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaShieldAlt } from "react-icons/fa";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import api from "../api";
import BackgroundImage from "../images/map4.jpg";
import Footer from "./Footer";

const steps = ["Validasi NIK", "Validasi OTP", "Buat Akun"];
const fieldSx = {
  "& .MuiFilledInput-root": { backgroundColor: "white", borderRadius: 2 },
  "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": { display: "none" },
};

const getErrorMessage = (error) =>
  error.response?.data?.message || error.response?.data?.msg || "Layanan sedang bermasalah. Silakan coba lagi.";

export const RegisterAkunMasyarakat = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState(0);
  const [nik, setNik] = useState("");
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const requestOtp = async (event) => {
    event?.preventDefault();
    const cleanNik = nik.replace(/\D/g, "");
    if (cleanNik.length !== 16) {
      setError("NIK harus terdiri dari 16 digit angka.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.post("/masyarakat/search-otp", { nik: cleanNik });
      setNik(cleanNik);
      setStep(1);
      setOtp("");
      setCooldown(60);
      setSuccess(response.data.message);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("OTP harus terdiri dari 6 digit angka.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/masyarakat/verify-otp", { nik, otp });
      setVerificationToken(response.data.data.verificationToken);
      setStep(2);
      setSuccess("Identitas berhasil divalidasi. Lengkapi kredensial akun Anda.");
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  const completeAccount = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Konfirmasi password tidak sama.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/masyarakat/complete-account", {
        nik,
        email: email.trim().toLowerCase(),
        password,
        verificationToken,
      });
      setStep(3);
      setSuccess("Akun berhasil dibuat. Silakan masuk menggunakan email dan password Anda.");
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: isMobile ? 2 : 4, backgroundImage: `linear-gradient(135deg, rgba(8, 47, 73, .82), rgba(15, 118, 110, .58)), url(${BackgroundImage})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
        <Paper elevation={12} sx={{ width: "100%", maxWidth: 520, p: isMobile ? 3 : 4.5, borderRadius: 4, bgcolor: "rgba(248, 250, 252, .96)" }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <img src="/images/logobaru.svg" alt="Logo SIDALIMAH" style={{ width: isMobile ? 64 : 78 }} />
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={800} color="#0f3d4c" mt={1}>Akun Penerima Bantuan</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>Validasi data penerima melalui WhatsApp sebelum membuat akun.</Typography>
          </Box>

          {step < 3 && <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>{steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}</Stepper>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {step === 0 && (
            <Box component="form" onSubmit={requestOtp}>
              <TextField fullWidth required label="NIK penerima bantuan" value={nik} onChange={(event) => setNik(event.target.value.replace(/\D/g, "").slice(0, 16))} inputProps={{ inputMode: "numeric", maxLength: 16 }} helperText={`${nik.length}/16 digit`} variant="filled" sx={fieldSx} />
              <Button type="submit" fullWidth size="large" variant="contained" disabled={loading} sx={{ mt: 2, py: 1.4, bgcolor: "#0f766e" }}>{loading ? <CircularProgress size={24} color="inherit" /> : "Kirim OTP ke WhatsApp"}</Button>
            </Box>
          )}

          {step === 1 && (
            <Box component="form" onSubmit={verifyOtp}>
              <Typography variant="body2" color="text.secondary" mb={2}>Masukkan kode 6 digit yang dikirim ke nomor WhatsApp pada data bantuan NIK {nik.replace(/^(\d{4})\d{8}(\d{4})$/, "$1********$2")}.</Typography>
              <TextField fullWidth required autoFocus label="Kode OTP" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} inputProps={{ inputMode: "numeric", maxLength: 6, style: { letterSpacing: "0.5em", textAlign: "center" } }} variant="filled" sx={fieldSx} />
              <Button type="submit" fullWidth size="large" variant="contained" disabled={loading} sx={{ mt: 2, py: 1.4, bgcolor: "#0f766e" }}>{loading ? <CircularProgress size={24} color="inherit" /> : "Validasi OTP"}</Button>
              <Button fullWidth disabled={loading || cooldown > 0} onClick={requestOtp} sx={{ mt: 1, textTransform: "none" }}>{cooldown > 0 ? `Kirim ulang dalam ${cooldown} detik` : "Kirim ulang OTP"}</Button>
              <Button fullWidth onClick={() => { setStep(0); setError(""); setSuccess(""); }} sx={{ textTransform: "none" }}>Ganti NIK</Button>
            </Box>
          )}

          {step === 2 && (
            <Box component="form" onSubmit={completeAccount}>
              <TextField fullWidth required type="email" label="Email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" variant="filled" sx={fieldSx} />
              <TextField fullWidth required label="Password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" helperText="Minimal 8 karakter" variant="filled" margin="normal" sx={fieldSx} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"} onClick={() => setShowPassword((value) => !value)}>{showPassword ? <FaEyeSlash /> : <FaEye />}</IconButton></InputAdornment> }} />
              <TextField fullWidth required label="Konfirmasi password" type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" variant="filled" sx={fieldSx} />
              <Button type="submit" fullWidth size="large" variant="contained" disabled={loading} startIcon={!loading && <FaShieldAlt />} sx={{ mt: 2, py: 1.4, bgcolor: "#0f766e" }}>{loading ? <CircularProgress size={24} color="inherit" /> : "Buat Akun Masyarakat"}</Button>
            </Box>
          )}

          {step === 3 && (
            <Box sx={{ textAlign: "center" }}>
              <FaShieldAlt size={48} color="#0f766e" />
              <Typography variant="h6" fontWeight={700} mt={1}>Pendaftaran Selesai</Typography>
              <Alert severity="info" sx={{ mt: 2, textAlign: "left" }}>
                Saat login, gunakan NIK <strong>{nik}</strong> sebagai username dan gunakan password yang baru saja Anda buat.
              </Alert>
              <Button fullWidth size="large" variant="contained" onClick={() => navigate("/login", { replace: true })} sx={{ mt: 3, py: 1.4, bgcolor: "#0f766e" }}>Ke Halaman Login</Button>
            </Box>
          )}

          {step !== 3 && <Button fullWidth onClick={() => navigate("/login")} sx={{ mt: 1.5, textTransform: "none", color: "text.secondary" }}>Kembali ke login</Button>}
        </Paper>
      </Box>
      <Footer />
    </>
  );
};
